import { InputRule, mergeAttributes, Node } from '@tiptap/core';
import {
	humaniseFieldName,
	NAMED_FIELD_TOKEN_PATTERN,
} from '@dealstack/shared/lib/common/business/documentTemplate';

export const FIELD_NODE_NAME = 'field';

export interface FieldNodeOptions {
	/** The data point's own label, e.g. `investor.first_name` → "First name". */
	labelFor?: (name: string) => string | undefined;
	/**
	 * Whether a value will actually be rendered for this name.
	 *
	 * Absent = nothing is known about any name, so no field is marked. Deliberately separate
	 * from `labelFor`: a name can be missing a label and still be bound.
	 */
	isBound?: (name: string) => boolean;
}

/**
 * A bound data point, as ONE atomic inline node.
 *
 * `atom: true` is the load-bearing part. A token held as text can be split by an editor, have
 * its quotes reformatted by autocorrect, or be typo'd into a field nobody linked — each of
 * which renders as a blank in an executed deed. As an atom the caret steps over it, a single
 * backspace removes the whole thing, and the name is data rather than spelling.
 */
export const FieldNode = Node.create<FieldNodeOptions>({
	name: FIELD_NODE_NAME,
	group: 'inline',
	inline: true,
	atom: true,
	selectable: true,
	draggable: true,

	addOptions() {
		return {};
	},

	addAttributes() {
		return {
			name: {
				default: '',
				parseHTML: (element) => element.getAttribute('data-field'),
				renderHTML: (attributes) => ({ 'data-field': attributes.name }),
			},
			// The filter keeps its ARGUMENTS verbatim (`currency: "GBP"`), held as an attribute
			// where no editor can turn its quotes into smart quotes.
			filter: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-filter'),
				renderHTML: (attributes) =>
					attributes.filter === null ? {} : { 'data-filter': attributes.filter },
			},
		};
	},

	/**
	 * A token typed by hand becomes a node the moment it is closed.
	 *
	 * Without this the `{{` picker was the ONLY way to make a field: type
	 * `{{ investor.first_name }}` yourself and it stayed text, which meant it was never offered
	 * for linking — the scan reads the node tree — and was reported as an unusable name. The
	 * rule is anchored to the caret, so it fires on the second closing brace.
	 */
	addInputRules() {
		const type = this.type;
		return [
			new InputRule({
				find: new RegExp(`${NAMED_FIELD_TOKEN_PATTERN}$`),
				// A plain InputRule, not `nodeInputRule`: that one narrows the replaced range to
				// capture group 1, which here is the NAME — so it swapped the name for a field
				// and left the braces sitting either side of it as text.
				handler: ({ state, range, match }) => {
					const name = match[1];
					if (name === undefined) {
						return null;
					}
					const filter = match[2];
					state.tr.replaceWith(
						range.from,
						range.to,
						type.create({ name, filter: filter ?? null })
					);
					return undefined;
				},
			}),
		];
	},

	parseHTML() {
		return [{ tag: 'span[data-field]' }];
	},

	/**
	 * Drawn as the data point's LABEL, not as its token.
	 *
	 * `{{ investor.first_name_2 }}` on the page told the author about the model's naming rules
	 * and nothing about the deed: the name is an internal key, the suffix is a de-duplication
	 * artefact, and neither is something a person reading a share transfer should have to
	 * decode. The name stays on `data-field`, which is what the document is saved from.
	 */
	renderHTML({ HTMLAttributes, node }) {
		const name = String(node.attrs.name);
		const { labelFor, isBound } = this.options;
		// A name nothing is bound to renders as a BLANK. Drawn in the bound colour it was
		// indistinguishable from a field that would be filled in, so a variable typed wrong read
		// as finished work and shipped that way.
		const unbound = isBound !== undefined && !isBound(name);
		return [
			'span',
			mergeAttributes(HTMLAttributes, {
				class: unbound ? 'ds-field ds-field--unbound' : 'ds-field',
			}),
			labelFor?.(name) ?? humaniseFieldName(name),
		];
	},

	/** What a copy-paste out of the editor, or a plain-text export, should read as. */
	renderText({ node }) {
		const filter = node.attrs.filter;
		return `{{ ${String(node.attrs.name)}${filter ? ` | ${String(filter)}` : ''} }}`;
	},
});
